import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { logEvent } from '../../utils/analytics';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../../utils/axios';
import './CompleteProfilePage.css'; // 將創建這個檔案

const CompleteProfilePage = () => {
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'info' });
  const { user, isAuthenticated, loading, login } = useAuth();
  const navigate = useNavigate();

  // 設置頁面標題
  useEffect(() => {
    logEvent('auth_gate_view', { status: 'complete_profile' });
  }, []);

  useEffect(() => {
    document.title = '完成個人資料';
  }, []);

  // 檢查用戶登入狀態和資料完成狀態
  useEffect(() => {
    if (!loading && isAuthenticated) {
      if (user && user.isActive) {
        // 用戶已激活，重定向到首頁
        navigate('/');
      } else if (user && user.additionalInfo && user.additionalInfo.trim() !== '') {
        // 用戶已填寫資料但尚未激活，重定向到等待審核頁面
        navigate('/pending');
      }
      // 如果用戶已登入但沒有填寫資料，則留在當前頁面
    } else if (!loading && !isAuthenticated) {
      // 使用 login 函數觸發登入流程
      login();
    }
  }, [loading, isAuthenticated, navigate, login, user]);

  // 提交附加信息
  const submitAdditionalInfo = async (additionalInfo) => {
    try {
      await apiClient.patch('/users/me', { additionalInfo });
      
      return { 
        success: true, 
        message: '資料已提交，等待管理員審核'
      };
    } catch (error) {
      // 根據狀態碼返回不同的錯誤訊息
      const status = error.response?.status;
      let message = '提交失敗，請稍後再試';
      
      if (status === 400) {
        message = '請填寫有效的個人資料';
      } else if (status === 401) {
        message = '請先登入';
      } else if (status === 500) {
        message = '伺服器錯誤，請稍後再試';
      }
      
      return {
        success: false,
        message
      };
    }
  };

  // 處理表單提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!additionalInfo.trim()) {
      setAlert({
        show: true,
        message: '請填寫個人資料',
        variant: 'danger'
      });
      return;
    }
    
    setIsSubmitting(true);
    setAlert({ show: false });
    
    try {
      const result = await submitAdditionalInfo(additionalInfo);
      
      if (result.success) {
        // 設置標記，表示用戶已經提交過資料
        localStorage.setItem('profile-submitted', 'true');
        
        setAlert({
          show: true,
          message: result.message,
          variant: 'success'
        });
        
        // 3秒後導向到等待審核頁面
        setTimeout(() => {
          navigate('/pending');
        }, 3000);
      } else {
        setAlert({
          show: true,
          message: result.message,
          variant: 'danger'
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: '提交表單時發生錯誤，請稍後再試',
        variant: 'danger'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="complete-profile-container">
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
          <p>載入中，請稍候...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="complete-profile-container">
      <div className="profile-card">
        <div className="profile-header">
          完成個人資料
        </div>
        <div className="profile-body">
          {alert.show && (
            <Alert 
              variant={alert.variant} 
              dismissible 
              onClose={() => setAlert({ show: false })}
              className="profile-alert"
            >
              <p>{alert.message}</p>
            </Alert>
          )}
          
          <div className="profile-intro">
            <p>感謝您登入！為了提供更好的服務，請填寫以下附加資料。管理員審核後您的帳號將被啟用。</p>
          </div>
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="profile-form-group">
              <Form.Label className="profile-form-label">個人資料與用途說明</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="請提供更多關於您的資料，例如：您為何需要存取本系統、您的用途、您喜歡的成員等..."
                required
                className="profile-textarea"
              />
              <Form.Text className="profile-form-text">
                您的資料只會用於帳號審核目的，不會用於其他用途。
              </Form.Text>
            </Form.Group>
            
            <div className="profile-button-container">
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                className="profile-submit-button"
              >
                {isSubmitting ? '提交中...' : '提交資料'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;